import { useState, useEffect, useCallback, useRef } from "react";
import { PlusCircle, LayoutGrid, LayoutList, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import SearchBar from "../components/SearchBar";
import WillCard from "../components/WillCard.tsx";
import WillGrid from "../components/WillGrid";
import { WillFeedParams, searchWills, Will } from '../services/api';
import { useDebounce } from "../hooks/useDebounce";
import { useScrollPosition } from '../contexts/ScrollPositionContext';
import ScrollDebug from '../components/ScrollDebug';
const Index = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [wills, setWills] = useState<Will[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WillFeedParams>({
    category: undefined,
    location: undefined,
    maxBudget: undefined
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const {
    getScrollPosition,
    clearScrollPosition
  } = useScrollPosition();
  const scrollRestored = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!scrollRestored.current) {
        const savedPosition = getScrollPosition('feedScroll');
        if (savedPosition > 0) {
          window.scrollTo(0, savedPosition);
          scrollRestored.current = true;
          console.log('Restored scroll position to:', savedPosition);
        }
      }
    }, 100);
    return () => {
      clearTimeout(timer);
      if (!window.location.pathname.includes('/will/')) {
        clearScrollPosition('feedScroll');
      }
    };
  }, [getScrollPosition, clearScrollPosition]);
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setWills([]);
      setHasMore(true);
      setCursor(null);
      return;
    }
    if (term.length < 2) {
      setWills([]);
      setHasMore(true);
      setCursor(null);
      return;
    }
    setLoading(true);
    try {
      const response = await searchWills(term);
      setWills(response.content);
      setHasMore(response.hasMore);
      if (response.content.length > 0) {
        setCursor(response.content[response.content.length - 1].createdAt);
      }
    } catch (error) {
      console.error('Search failed:', error);
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);
  const debouncedSearch = useDebounce((term: string) => {
    performSearch(term);
  }, 300);
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  };
  const handleFilterChange = (newFilters: WillFeedParams) => {
    setFilters(newFilters);
  };
  const mapStatusToUrgency = (status: string, fulfillmentTime?: string): "low" | "medium" | "high" => {
    if (!fulfillmentTime) return "low";
    const dueDate = new Date(fulfillmentTime);
    const today = new Date();
    const oneWeek = new Date();
    oneWeek.setDate(today.getDate() + 7);
    return dueDate <= oneWeek ? "high" : "medium";
  };
  const getCurrencySymbol = (currency: string | null): string => {
    const symbolMap: {
      [key: string]: string;
    } = {
      'ILS': '₪',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    return currency ? symbolMap[currency] || '₪' : '₪';
  };
  if (error) {
    return <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 pt-24 text-center text-red-600">
          {error}
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 pt-24 pb-12">
        <header className="text-center mb-12">
          <div className="flex flex-col items-center justify-center mb-8">
            <h1 className="text-5xl font-bold text-gray-900 flex items-center">
              <span className="font-['Rye'] mr-0 text-black transform scale-y-110 uppercase tracking-wider text-7xl font-thin">W</span>
              <span>ANTTD</span>
            </h1>
            <div className="mt-4 flex gap-4">
              <Link to="/create-will" className="flex items-center gap-2 px-6 py-3 bg-primary text-gray-900 font-semibold rounded-full hover:bg-primary/90 transition-colors">
                <PlusCircle className="h-5 w-5" />
                I want...
              </Link>
              
              <Link to="/feed" className="flex items-center gap-2 px-6 py-3 bg-black text-white font-semibold rounded-full hover:bg-gray-800 transition-colors">
                <Instagram className="h-5 w-5" />
                Try new Instagram feed
              </Link>
            </div>
          </div>
          <p className="text-gray-600 max-w-2xl mx-auto px-0 py-[20px]">
            What do you LOVE to do? Browse people's wants, become a Genie, and fulfill someone's want or wish!
            Bringing happiness again and again. And one more thing… 
            Become happy yourself by doing what you LOVE and getting rewarded along the way
          </p>
        </header>

        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <SearchBar onSearch={handleSearch} />
            <div className="flex gap-2">
              <button onClick={() => setViewMode("list")} className={`p-2 rounded-lg transition-colors ${viewMode === "list" ? "bg-primary text-gray-900" : "bg-gray-100 text-gray-600"}`}>
                <LayoutList className="h-5 w-5" />
              </button>
              <button onClick={() => setViewMode("grid")} className={`p-2 rounded-lg transition-colors ${viewMode === "grid" ? "bg-primary text-gray-900" : "bg-gray-100 text-gray-600"}`}>
                <LayoutGrid className="h-5 w-5" />
              </button>
            </div>
          </div>

          {loading ? <div className="text-center py-8">Loading wills...</div> : searchQuery.length === 1 ? <div>
              <div className="text-center text-gray-600 mb-4 py-0">
                Please type one more character to search...
              </div>
              <WillGrid filters={filters} onFilterChange={handleFilterChange} viewMode={viewMode} />
            </div> : searchQuery.length > 1 ? <div className={viewMode === "list" ? "flex flex-col space-y-4 max-w-3xl mx-auto" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"}>
              {wills.length === 0 ? <div className="col-span-full text-center py-8 text-gray-600">
                  No results found for "{searchQuery}"
                </div> : wills.map(will => <WillCard key={will.id} id={will.id} title={will.title} description={will.description} category={will.category} urgency={mapStatusToUrgency(will.status, will.fulfillmentTime)} reward={will.budget ? `${getCurrencySymbol(will.currency)}${will.budget}` : null} fulfillmentTime={will.fulfillmentTime || ''} wanterName={will.wanterName} createdAt={will.createdAt} location={will.location || ''} status={will.status} wanterId={will.wanterId} viewMode={viewMode} className={viewMode === "grid" ? "h-full" : "w-full"} />)}
            </div> : <WillGrid filters={filters} onFilterChange={handleFilterChange} viewMode={viewMode} />}
        </div>
      </main>
      
      {process.env.NODE_ENV !== 'production' && <ScrollDebug />}
    </div>;
};
export default Index;